// TranslatedTabContent.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Tooltip, message, Segmented, Modal, Checkbox } from 'antd';
import { CopyOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { TranslatedChapter, SystemProofreaderSummary } from './types';
import { removeHyperlinks } from './ProofreadTabContent';
import { formatFrenchNarrativeStyle } from './utils/frenchToneHelpers';
import { getAuthHeaders } from './utils/authHeaders';
import { getQuotationStyle } from './LanguageConfig';
import { getSavedProofreadTextForRow } from './utils/proofreadState';
import { isLocalFrontendHostname } from './utils/apiBase';

interface TranslatedTabContentProps {
  translatedChapter?: TranslatedChapter;
  sourceLang: string;
  targetLang: string;
  sourceChapterText?: string;
  bookId?: string;
  chapterGuid?: string;
  isAdmin?: boolean;
  apiBase?: string;
  tabActionsContainer?: HTMLElement | null;
  isActive?: boolean;
  onDelete: (sourceLang: string, targetLang: string) => void;
}

interface TranslationStageTraceResponse {
  traceSummary?: Record<string, any> | null;
  eventCount?: number;
  events?: Array<Record<string, any>>;
}

type AdminCopySection = {
  key: string;
  label: string;
  value: string;
  available: boolean;
  category: 'base' | 'rules' | 'responses';
};

const ADMIN_COPY_SECTION_DIVIDER = '='.repeat(60);
// Temporarily hidden in the app; tests still exercise this diagnostic path.
const ADMIN_STAGE_TRACE_UI_ENABLED =
  process.env.NODE_ENV === 'test' &&
  isLocalFrontendHostname(
    typeof window !== 'undefined' ? window.location.hostname : undefined,
  );

const toFiniteNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatUsdCompact = (value: number): string => {
  const normalized = toFiniteNumber(value);
  return `$${normalized.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 5,
  })}`;
};

const getEventTokenCounts = (event: Record<string, any>): Record<string, any> => {
  const raw = (event?.token_counts || event?.tokenCounts || {}) as Record<string, any>;
  return raw && typeof raw === 'object' ? raw : {};
};

const getEventTokenUsageTotals = (event: Record<string, any>): {
  input: number;
  output: number;
  cached: number;
} => {
  const tokenCounts = getEventTokenCounts(event);
  return {
    input: toFiniteNumber(
      tokenCounts.inputTokens ?? tokenCounts.input_tokens ?? tokenCounts.prompt_tokens ?? tokenCounts.input,
    ),
    output: toFiniteNumber(
      tokenCounts.outputTokens ?? tokenCounts.output_tokens ?? tokenCounts.completion_tokens ?? tokenCounts.output,
    ),
    cached: toFiniteNumber(
      tokenCounts.cachedTokens ?? tokenCounts.cached_tokens ?? tokenCounts.cached ?? tokenCounts.cached_input_tokens,
    ),
  };
};

const hasUnpricedTokenUsage = (event: Record<string, any>): boolean => {
  const usage = getEventTokenUsageTotals(event);
  const hasBillableUsage = usage.input > 0 || usage.output > 0 || usage.cached > 0;
  if (!hasBillableUsage) {
    return false;
  }
  const costBreakdown = (event?.cost_breakdown || event?.costBreakdown || null) as Record<string, any> | null;
  const costUsd = (costBreakdown?.costUsd || costBreakdown?.cost_usd || null) as Record<string, any> | null;
  if (!costUsd || typeof costUsd !== 'object') {
    return true;
  }
  const totalCost = toFiniteNumber(costUsd.total);
  const inputCost = toFiniteNumber(costUsd.input);
  const outputCost = toFiniteNumber(costUsd.output);
  const cachedInputCost = toFiniteNumber(costUsd.cachedInput ?? costUsd.cached_input);
  return totalCost <= 0 && inputCost <= 0 && outputCost <= 0 && cachedInputCost <= 0;
};

const buildStageCostSummary = (event: Record<string, any>): string => {
  const costBreakdown = (event?.cost_breakdown || event?.costBreakdown || {}) as Record<string, any>;
  const costUsd = (costBreakdown?.costUsd || costBreakdown?.cost_usd || {}) as Record<string, any>;
  const inputCost = toFiniteNumber(costUsd.input);
  const cachedInputCost = toFiniteNumber(costUsd.cachedInput ?? costUsd.cached_input);
  const outputCost = toFiniteNumber(costUsd.output);
  const reasoningOutputCost = toFiniteNumber(
    costUsd.reasoningOutput ?? costUsd.reasoning_output,
  );
  const nonReasoningOutputCost = toFiniteNumber(
    costUsd.nonReasoningOutput ?? costUsd.non_reasoning_output ?? costUsd.outputNonReasoning,
  );
  const totalCost = toFiniteNumber(
    costUsd.total || inputCost + cachedInputCost + outputCost,
  );

  if (totalCost <= 0 && inputCost <= 0 && outputCost <= 0 && cachedInputCost <= 0) {
    return '';
  }

  const cachedSegment =
    cachedInputCost > 0 ? ` (+ ${formatUsdCompact(cachedInputCost)} cached input)` : '';
  const outputDetailSegment =
    reasoningOutputCost > 0 || nonReasoningOutputCost > 0
      ? ` (${formatUsdCompact(reasoningOutputCost)} reasoning, ${formatUsdCompact(nonReasoningOutputCost)} output)`
      : '';

  return `${formatUsdCompact(inputCost)} input${cachedSegment} - ${formatUsdCompact(outputCost)} output${outputDetailSegment} - ${formatUsdCompact(totalCost)} total`;
};

const buildStageCostCoverageSummary = (
  events: Array<Record<string, any>>,
): {
  unpricedEventCount: number;
  unpricedStageNames: string[];
} => {
  const unpricedStageNames: string[] = [];
  events.forEach((event) => {
    if (!hasUnpricedTokenUsage(event)) {
      return;
    }
    const stageName = String(event?.stage || 'unknown_stage');
    if (!unpricedStageNames.includes(stageName)) {
      unpricedStageNames.push(stageName);
    }
  });
  return {
    unpricedEventCount: events.filter((event) => hasUnpricedTokenUsage(event)).length,
    unpricedStageNames,
  };
};

const buildTotalStageCostSummary = (
  events: Array<Record<string, any>>,
): string => {
  const totals = events.reduce(
    (acc, event) => {
      const costBreakdown = (event?.cost_breakdown || event?.costBreakdown || {}) as Record<string, any>;
      const costUsd = (costBreakdown?.costUsd || costBreakdown?.cost_usd || {}) as Record<string, any>;
      const inputCost = toFiniteNumber(costUsd.input);
      const cachedInputCost = toFiniteNumber(costUsd.cachedInput ?? costUsd.cached_input);
      const outputCost = toFiniteNumber(costUsd.output);
      acc.input += inputCost;
      acc.cachedInput += cachedInputCost;
      acc.output += outputCost;
      acc.reasoning += toFiniteNumber(
        costUsd.reasoningOutput ?? costUsd.reasoning_output,
      );
      acc.nonReasoningOutput += toFiniteNumber(
        costUsd.nonReasoningOutput ?? costUsd.non_reasoning_output ?? costUsd.outputNonReasoning,
      );
      acc.total += toFiniteNumber(
        costUsd.total || inputCost + cachedInputCost + outputCost,
      );
      return acc;
    },
    { input: 0, cachedInput: 0, output: 0, reasoning: 0, nonReasoningOutput: 0, total: 0 },
  );

  if (
    totals.total <= 0
    && totals.input <= 0
    && totals.output <= 0
    && totals.cachedInput <= 0
  ) {
    return '';
  }

  const cachedSegment =
    totals.cachedInput > 0 ? ` (+ ${formatUsdCompact(totals.cachedInput)} cached input)` : '';
  const outputDetailSegment =
    totals.reasoning > 0 || totals.nonReasoningOutput > 0
      ? ` (${formatUsdCompact(totals.reasoning)} reasoning, ${formatUsdCompact(totals.nonReasoningOutput)} output)`
      : '';

  return `${formatUsdCompact(totals.input)} input${cachedSegment} - ${formatUsdCompact(totals.output)} output${outputDetailSegment} - ${formatUsdCompact(totals.total)} total`;
};

const getEventTextField = (event: Record<string, any>, keys: string[]): string => {
  for (const key of keys) {
    const value = event?.[key];
    if (value === undefined || value === null) {
      continue;
    }
    const text = String(value || '').trim();
    if (text) {
      return text;
    }
  }
  return '';
};

const formatAdminTraceValue = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value.trim();
  try {
    return JSON.stringify(value, null, 2).trim();
  } catch (_err) {
    return String(value).trim();
  }
};

const getFirstTraceTextByStage = (
  events: Array<Record<string, any>>,
  stageNames: string[],
  fieldKeys: string[],
): string => {
  const normalizedStageNames = Array.from(
    new Set(
    stageNames.map((name) => String(name || '').trim().toLowerCase()),
    ),
  );
  for (const event of events || []) {
    const stage = String(event?.stage || '').trim().toLowerCase();
    const isStageMatch = normalizedStageNames.some(
      (candidate) =>
        stage === candidate ||
        stage.startsWith(`${candidate}_`) ||
        stage.startsWith(`${candidate}(`) ||
        stage.startsWith(`${candidate} `),
    );
    if (!isStageMatch) {
      continue;
    }
    const text = getEventTextField(event, fieldKeys);
    if (text) {
      return text;
    }
  }
  return '';
};

const getLatestTraceTextByStage = (
  events: Array<Record<string, any>>,
  stageNames: string[],
  fieldKeys: string[],
): string => {
  const normalizedStageNames = Array.from(
    new Set(
      stageNames.map((name) => String(name || '').trim().toLowerCase()),
    ),
  );
  for (let eventIndex = (events || []).length - 1; eventIndex >= 0; eventIndex -= 1) {
    const event = (events || [])[eventIndex];
    const stage = String(event?.stage || '').trim().toLowerCase();
    const isStageMatch = normalizedStageNames.some(
      (candidate) =>
        stage === candidate ||
        stage.startsWith(`${candidate}_`) ||
        stage.startsWith(`${candidate}(`) ||
        stage.startsWith(`${candidate} `),
    );
    if (!isStageMatch) {
      continue;
    }

    const rawResponses = event?.raw_responses ?? event?.rawResponses;
    if (Array.isArray(rawResponses)) {
      for (let responseIndex = rawResponses.length - 1; responseIndex >= 0; responseIndex -= 1) {
        const entry = rawResponses[responseIndex];
        if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
          continue;
        }
        const entryText = getEventTextField(entry as Record<string, any>, fieldKeys);
        if (entryText) {
          return entryText;
        }
      }
    }

    const eventText = getEventTextField(event, fieldKeys);
    if (eventText) {
      return eventText;
    }
  }
  return '';
};

const getAllTraceResponsesByStage = (
  events: Array<Record<string, any>>,
  stageNames: string[],
): string => {
  const normalizedStageNames = Array.from(
    new Set(stageNames.map((name) => String(name || '').trim().toLowerCase())),
  );
  const formattedEntries: string[] = [];
  let sequence = 1;

  const pushFormattedEntry = (
    value: unknown,
    meta?: { run?: unknown; pass?: unknown; phase?: unknown; status?: unknown },
  ) => {
    const payload = formatAdminTraceValue(value);
    if (!payload) return;
    const tags: string[] = [];
    if (meta?.run !== undefined && meta?.run !== null && String(meta.run).trim()) {
      tags.push(`run=${String(meta.run).trim()}`);
    }
    if (meta?.pass !== undefined && meta?.pass !== null && String(meta.pass).trim()) {
      tags.push(`pass=${String(meta.pass).trim()}`);
    }
    if (meta?.phase !== undefined && meta?.phase !== null && String(meta.phase).trim()) {
      tags.push(`phase=${String(meta.phase).trim()}`);
    }
    if (meta?.status !== undefined && meta?.status !== null && String(meta.status).trim()) {
      tags.push(`status=${String(meta.status).trim()}`);
    }
    const header = tags.length > 0 ? `[response ${sequence} | ${tags.join(' | ')}]` : `[response ${sequence}]`;
    formattedEntries.push(`${header}\n${payload}`);
    sequence += 1;
  };

  for (const event of events || []) {
    const stage = String(event?.stage || '').trim().toLowerCase();
    const isStageMatch = normalizedStageNames.some(
      (candidate) =>
        stage === candidate ||
        stage.startsWith(`${candidate}_`) ||
        stage.startsWith(`${candidate}(`) ||
        stage.startsWith(`${candidate} `),
    );
    if (!isStageMatch) continue;

    const rawResponses = event?.raw_responses ?? event?.rawResponses;
    if (Array.isArray(rawResponses) && rawResponses.length > 0) {
      rawResponses.forEach((entry) => {
        if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
          pushFormattedEntry(
            (entry as Record<string, unknown>).raw_response
              ?? (entry as Record<string, unknown>).rawResponse
              ?? entry,
            {
              run:
                (entry as Record<string, unknown>).run_index
                ?? (entry as Record<string, unknown>).runIndex
                ?? event?.friction_run_index
                ?? event?.frictionRunIndex,
              pass:
                (entry as Record<string, unknown>).pass_index
                ?? (entry as Record<string, unknown>).passIndex,
              phase: (entry as Record<string, unknown>).phase,
              status: (entry as Record<string, unknown>).status,
            },
          );
        } else {
          pushFormattedEntry(entry);
        }
      });
      continue;
    }

    const fallbackRaw = event?.raw_response ?? event?.rawResponse;
    if (fallbackRaw !== undefined && fallbackRaw !== null) {
      pushFormattedEntry(fallbackRaw, {
        run: event?.friction_run_index ?? event?.frictionRunIndex,
        status: event?.status,
      });
    }
  }

  return formattedEntries.join('\n\n');
};

const normalizePositiveIndex = (value: unknown): number | null => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return null;
  }
  return parsed;
};

const buildStageLoopCount = (event: Record<string, any>): number => {
  const runIndexes = new Set<number>();
  const passIndexes = new Set<number>();

  const topRunIndex = normalizePositiveIndex(
    event?.friction_run_index ?? event?.frictionRunIndex,
  );
  if (topRunIndex !== null) {
    runIndexes.add(topRunIndex);
  }

  const topPassIndex = normalizePositiveIndex(event?.pass_index ?? event?.passIndex);
  if (topPassIndex !== null) {
    passIndexes.add(topPassIndex);
  }

  const rawResponses = event?.raw_responses ?? event?.rawResponses;
  if (Array.isArray(rawResponses)) {
    rawResponses.forEach((entry) => {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        return;
      }
      const candidate = entry as Record<string, unknown>;
      const runIndex = normalizePositiveIndex(
        candidate.run_index ?? candidate.runIndex,
      );
      if (runIndex !== null) {
        runIndexes.add(runIndex);
      }
      const passIndex = normalizePositiveIndex(
        candidate.pass_index ?? candidate.passIndex,
      );
      if (passIndex !== null) {
        passIndexes.add(passIndex);
      }
    });
  }

  if (runIndexes.size > 0) {
    return runIndexes.size;
  }
  if (passIndexes.size > 0) {
    return passIndexes.size;
  }
  const frictionRunsExecuted = normalizePositiveIndex(
    event?.friction_runs_executed ?? event?.frictionRunsExecuted,
  );
  if (frictionRunsExecuted !== null) {
    return frictionRunsExecuted;
  }
  return 1;
};

const isJapaneseLanguage = (lang?: string) => {
  if (!lang) return false;
  const trimmed = lang.trim();
  const lowerCased = trimmed.toLowerCase();
  return (
    lowerCased.includes('japanese') ||
    lowerCased.startsWith('ja') ||
    lowerCased.startsWith('jp') ||
    trimmed.includes('日本語')
  );
};

const containsJapaneseCharacters = (text?: string) => {
  if (!text) return false;
  return /[\u3040-\u30ff\u31f0-\u31ff\u3400-\u4dbf\u4e00-\u9fff\uF900-\uFAFF\uFF66-\uFF9F]/.test(text);
};

const KNOWN_MODEL_DISPLAY_FALLBACKS: Record<string, string> = {
  'gpt5test': 'ChatGPT-5',
  'gpt-5-2025-08-07': 'ChatGPT-5',
  'chatgpt-5': 'ChatGPT-5',
};

const resolveKnownModelDisplay = (value?: string): string => {
  const key = (value || '').trim().toLowerCase();
  if (!key) return '';
  return KNOWN_MODEL_DISPLAY_FALLBACKS[key] || '';
};

const normalizeChoice = (value?: string | null): string =>
  String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ');

const resolveWorkflowModeLabel = (value?: string | null): string => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'easy') return 'Quick setup';
  if (normalized === 'advanced') return 'Advanced';
  return '';
};

const resolveQuotationStyleLabel = (
  language: string,
  quotationStyle?: string | null,
): string => {
  const raw = String(quotationStyle || '').trim();
  if (!raw) return '';
  const config = getQuotationStyle(language);
  const options = Array.isArray(config?.options) ? config?.options ?? [] : [];
  const normalizedRaw = normalizeChoice(raw);
  const selectedOption = options.find(
    (option) => normalizeChoice(option?.value) === normalizedRaw,
  );
  if (!selectedOption) {
    return raw;
  }
  return selectedOption.marks
    ? `${selectedOption.label} (${selectedOption.marks})`
    : selectedOption.label;
};

const TranslatedTabContent: React.FC<TranslatedTabContentProps> = ({
  translatedChapter,
  sourceLang,
  targetLang,
  sourceChapterText,
  bookId,
  chapterGuid,
  isAdmin,
  apiBase,
  tabActionsContainer,
  isActive = true,
  onDelete,
}) => {
  const savedProofreadText = useMemo(
    () => getSavedProofreadTextForRow(translatedChapter),
    [translatedChapter],
  );

  const safeHtml = removeHyperlinks((translatedChapter?.translated_text || '').replace(/[\u200B-\u200D\uFEFF]/g, ''));
  const [writingDirection, setWritingDirection] = useState<'horizontal' | 'vertical'>('horizontal');
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceError, setTraceError] = useState('');
  const [traceSummary, setTraceSummary] = useState<Record<string, any> | null>(null);
  const [traceEvents, setTraceEvents] = useState<Array<Record<string, any>>>([]);
  const [isAdminCopyModalOpen, setIsAdminCopyModalOpen] = useState(false);
  const [selectedAdminCopyKeys, setSelectedAdminCopyKeys] = useState<string[]>([]);

  const showOrientationToggle = useMemo(
    () => isJapaneseLanguage(targetLang),
    [targetLang]
  );
  const canLoadStageTraces = Boolean(
    ADMIN_STAGE_TRACE_UI_ENABLED &&
      isAdmin &&
      apiBase &&
      bookId &&
      chapterGuid &&
      sourceLang &&
      targetLang
  );

  useEffect(() => {
    let cancelled = false;
    if (!canLoadStageTraces) {
      setTraceSummary(null);
      setTraceEvents([]);
      setTraceError('');
      setTraceLoading(false);
      return () => {
        cancelled = true;
      };
    }
    const fetchStageTraces = async () => {
      try {
        setTraceLoading(true);
        setTraceError('');
        const headers = await getAuthHeaders();
        const params = new URLSearchParams({
          source_lang: sourceLang,
          target_lang: targetLang,
          limit: '1200',
        });
        const response = await fetch(
          `${apiBase}/admin/books/${bookId}/chapters/${chapterGuid}/translation-stage-traces?${params.toString()}`,
          { headers }
        );
        const payload = (await response.json()) as TranslationStageTraceResponse & {
          detail?: string;
        };
        if (!response.ok) {
          throw new Error(payload?.detail || 'Unable to load translation stage traces.');
        }
        if (cancelled) return;
        const fetchedSummary = (payload?.traceSummary as Record<string, any>) || null;
        const fetchedEvents = Array.isArray(payload?.events)
          ? (payload?.events as Array<Record<string, any>>)
          : [];
        setTraceSummary(fetchedSummary);
        setTraceEvents(fetchedEvents);
      } catch (err: any) {
        if (cancelled) return;
        setTraceSummary(null);
        setTraceEvents([]);
        const detail = err?.message || 'Unable to load translation stage traces.';
        setTraceError(String(detail));
      } finally {
        if (!cancelled) {
          setTraceLoading(false);
        }
      }
    };

    fetchStageTraces();
    return () => {
      cancelled = true;
    };
  }, [apiBase, bookId, chapterGuid, canLoadStageTraces, sourceLang, targetLang]);

  const formatDateTime = (isoString?: string | null) => {
    if (!isoString) return '';
    const parsed = new Date(isoString);
    if (Number.isNaN(parsed.getTime())) return '';
    return parsed.toLocaleString();
  };

  const buildStaleTooltip = (reason?: string | null, staleAt?: string | null) => {
    const reasonKey = (reason || '').toLowerCase();
    const primaryLine = reasonKey.includes('source')
      ? 'The source chapter was edited after this translation was generated.'
      : 'This translation is out of date with the source chapter.';
    const lines = [
      primaryLine,
      staleAt ? `Marked stale: ${staleAt}` : '',
      'Re-run translation to refresh this chapter.',
    ].filter(Boolean);
    return (
      <div>
        {lines.map((line, idx) => (
          <div key={`translation-stale-${idx}`}>{line}</div>
        ))}
      </div>
    );
  };

  const translatedOn = formatDateTime(translatedChapter?.translated_at);
  const selectionMode = (translatedChapter?.selection_mode || '').trim();
  const requestedModelId = (translatedChapter?.requested_model_id || '').trim();
  const recommendedModelId = (translatedChapter?.recommended_model_id || '').trim();
  const modelSelectionSuffix = (() => {
    if (selectionMode === 'auto') {
      return ' (Recommended Model)';
    }
    if (selectionMode === 'user') {
      if (requestedModelId && recommendedModelId && requestedModelId === recommendedModelId) {
        return ' (Recommended Model)';
      }
      return ' (user-selected)';
    }
    return '';
  })();
  const modelLine = translatedChapter?.api_model
    ? `Translation Model: ${translatedChapter.api_model}${modelSelectionSuffix}`
    : '';
  const translationModelValue = modelLine.replace(/^Translation Model:\s*/, '');
  const workflowModeLabel = resolveWorkflowModeLabel(translatedChapter?.workflow_mode);
  const quotationStyleLabel = resolveQuotationStyleLabel(
    translatedChapter?.target_language || targetLang,
    translatedChapter?.quotation_style,
  );
  const narrativeStyle = formatFrenchNarrativeStyle(translatedChapter?.narrative_style);
  const getProofreaderModel = (result?: SystemProofreaderSummary): string => {
    if (!result) return '';
    const legacyDisplayName = (result as any)?.model_display_name;
    const displayName = (result.modelDisplayName || legacyDisplayName || '').trim();
    if (displayName) return displayName;

    const modelId = (result.modelId || '').trim();
    const apiModel = (result.apiModel || '').trim();
    return (
      resolveKnownModelDisplay(modelId) ||
      resolveKnownModelDisplay(apiModel) ||
      modelId ||
      apiModel
    );
  };
  const autoFixModels = Array.from(
    new Set(
      [
        getProofreaderModel(translatedChapter?.system_proofreader),
        getProofreaderModel(translatedChapter?.system_general_proofreader),
      ].filter(Boolean)
    )
  );
  const proofreaderSurcharge = translatedChapter?.proofreader_surcharge_pct;
  const proofreaderStatusLine = (() => {
    const mode = translatedChapter?.proofreader_mode;
    if (!mode) return '';
    if (mode === 'included') {
      return 'Included with Recommended Model';
    }
    if (mode === 'user_enabled') {
      const surchargeLabel =
        typeof proofreaderSurcharge === 'number' ? ` (+${proofreaderSurcharge}% credits)` : '';
      return `Enabled by User${surchargeLabel}`;
    }
    if (mode === 'disabled') {
      return 'Off';
    }
    return '';
  })();
  const autoFixSummary = autoFixModels.join(' · ');
  const translationStaleAt = formatDateTime(translatedChapter?.translation_stale_at);
  const isTranslationStale = Boolean(translatedChapter?.translation_stale);
  const translationStaleBadge = isTranslationStale ? (
    <Tooltip
      title={buildStaleTooltip(translatedChapter?.translation_stale_reason, translationStaleAt || null)}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '2px 8px',
          borderRadius: 999,
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-warning, #d97706)',
          background: 'var(--surface-warning, #fff7ed)',
          border: '1px solid var(--border-warning, #f59e0b)',
        }}
      >
        Stale translation
      </span>
    </Tooltip>
  ) : null;

  const copyToClipboard = () => {
    try {
      const dummy = document.createElement('div');
      dummy.innerHTML = safeHtml;
      dummy.style.position = 'absolute';
      dummy.style.left = '-9999px';
      document.body.appendChild(dummy);

      const range = document.createRange();
      range.selectNode(dummy);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
        try {
          document.execCommand('copy');
          message.success('Translated text copied to clipboard');
        } catch (err) {
          message.error('Failed to copy text to clipboard');
        }
        selection.removeAllRanges();
      } else {
        message.error('Failed to access clipboard');
      }

      document.body.removeChild(dummy);
    } catch (e) {
      message.error('Failed to copy text to clipboard');
    }
  };

  const copyTraceSectionToClipboard = async (sectionLabel: string, text: string) => {
    const value = String(text || '');
    if (!value) {
      message.warning(`${sectionLabel} is empty.`);
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      message.success(`${sectionLabel} copied to clipboard.`);
    } catch (_err) {
      message.error(`Failed to copy ${sectionLabel.toLowerCase()}.`);
    }
  };

  const adminCopySections = useMemo((): AdminCopySection[] => {
    const sourceText = String(sourceChapterText || '').trim();
    const translatedText = String(
      translatedChapter?.translated_text || savedProofreadText || '',
    )
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .trim();

    const preTranslationRules = getFirstTraceTextByStage(
      traceEvents,
      ['pretranslation_dialogue_normalizer'],
      ['system_prompt', 'systemPrompt'],
    );
    const preTranslationReplacements = getAllTraceResponsesByStage(
      traceEvents,
      ['pretranslation_dialogue_normalizer'],
    );
    const postTranslationRules = getLatestTraceTextByStage(
      traceEvents,
      ['french_dialogue_normalizer'],
      ['system_prompt', 'systemPrompt', 'prompt', 'system'],
    );
    const postTranslationReplacements = getAllTraceResponsesByStage(
      traceEvents,
      ['french_dialogue_normalizer'],
    );
    const translationSystemPrompt = getFirstTraceTextByStage(
      traceEvents,
      ['translation_provider_call', 'translation'],
      ['system_prompt', 'systemPrompt'],
    );
    const formalityRules = getFirstTraceTextByStage(
      traceEvents,
      ['system_proofreader_formality'],
      ['system_prompt', 'systemPrompt'],
    );
    const formalityReplacements = getAllTraceResponsesByStage(
      traceEvents,
      ['system_proofreader_formality'],
    );
    const generalRules = getFirstTraceTextByStage(
      traceEvents,
      ['system_proofreader_general'],
      ['system_prompt', 'systemPrompt'],
    );
    const generalReplacements = getAllTraceResponsesByStage(
      traceEvents,
      ['system_proofreader_general'],
    );
    const frictionRules = getFirstTraceTextByStage(
      traceEvents,
      ['system_friction_rewriter', 'system_friction_detector'],
      ['system_prompt', 'systemPrompt'],
    );
    const frictionRawResponse = getFirstTraceTextByStage(
      traceEvents,
      ['system_friction_rewriter', 'system_friction_detector'],
      ['raw_response', 'rawResponse'],
    );
    const hasStageResponseArray = (stageNames: string[]): boolean => {
      const normalizedStageNames = stageNames.map((name) =>
        String(name || '').trim().toLowerCase(),
      );
      return (traceEvents || []).some((event) => {
        const stage = String(event?.stage || '').trim().toLowerCase();
        const isStageMatch = normalizedStageNames.some(
          (candidate) =>
            stage === candidate ||
            stage.startsWith(`${candidate}_`) ||
            stage.startsWith(`${candidate}(`) ||
            stage.startsWith(`${candidate} `),
        );
        if (!isStageMatch) return false;
        const rawResponses = event?.raw_responses ?? event?.rawResponses;
        return Array.isArray(rawResponses) && rawResponses.length > 0;
      });
    };
    const detectorHasArrayResponses = hasStageResponseArray(['system_friction_detector']);
    const rewriterHasArrayResponses = hasStageResponseArray(['system_friction_rewriter']);
    let frictionDetectorFindings = getAllTraceResponsesByStage(
      traceEvents,
      ['system_friction_detector'],
    );
    let frictionRewriterReplacements = getAllTraceResponsesByStage(
      traceEvents,
      ['system_friction_rewriter'],
    );
    if (!detectorHasArrayResponses && frictionRawResponse) {
      try {
        const parsed = JSON.parse(frictionRawResponse);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const detectValue = formatAdminTraceValue((parsed as Record<string, unknown>).detect);
          if (detectValue) {
            frictionDetectorFindings = detectValue;
          }
        } else {
          frictionDetectorFindings = frictionRawResponse.trim();
        }
      } catch (_err) {
        if (!frictionDetectorFindings) {
          frictionDetectorFindings = frictionRawResponse.trim();
        }
      }
    }
    if (!rewriterHasArrayResponses && frictionRawResponse) {
      try {
        const parsed = JSON.parse(frictionRawResponse);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const rewriteValue = formatAdminTraceValue((parsed as Record<string, unknown>).rewrite);
          if (rewriteValue) {
            frictionRewriterReplacements = rewriteValue;
          }
        }
      } catch (_err) {
        if (!frictionRewriterReplacements) {
          frictionRewriterReplacements = '';
        }
      }
    }

    return [
      {
        key: 'original_source_text',
        label: `${sourceLang || 'Source'} Source Text`,
        value: sourceText || '(not available)',
        available: Boolean(sourceText),
        category: 'base',
      },
      {
        key: 'translated_text',
        label: 'Translated Text',
        value: translatedText || '(not available)',
        available: Boolean(translatedText),
        category: 'base',
      },
      {
        key: 'pre_translation_rules',
        label: 'Pre-Translation Rules',
        value: preTranslationRules,
        available: Boolean(preTranslationRules),
        category: 'rules',
      },
      {
        key: 'pre_translation_replacements',
        label: 'Pre-Translation Replacements',
        value: preTranslationReplacements,
        available: Boolean(preTranslationReplacements),
        category: 'responses',
      },
      {
        key: 'post_translation_rules',
        label: 'French Dialogue Rules',
        value: postTranslationRules,
        available: Boolean(postTranslationRules),
        category: 'rules',
      },
      {
        key: 'post_translation_replacements',
        label: 'French Dialogue Replacements (All Loops)',
        value: postTranslationReplacements,
        available: Boolean(postTranslationReplacements),
        category: 'responses',
      },
      {
        key: 'translation_system_prompt',
        label: 'Translation System Prompt',
        value: translationSystemPrompt,
        available: Boolean(translationSystemPrompt),
        category: 'rules',
      },
      {
        key: 'formality_rules',
        label: 'Formality Rules',
        value: formalityRules,
        available: Boolean(formalityRules),
        category: 'rules',
      },
      {
        key: 'formality_replacements',
        label: 'Formality Replacements',
        value: formalityReplacements,
        available: Boolean(formalityReplacements),
        category: 'responses',
      },
      {
        key: 'general_proofread_rules',
        label: 'General Proofread Rules',
        value: generalRules,
        available: Boolean(generalRules),
        category: 'rules',
      },
      {
        key: 'general_proofread_replacements',
        label: 'General Proofread Replacements',
        value: generalReplacements,
        available: Boolean(generalReplacements),
        category: 'responses',
      },
      {
        key: 'friction_detector_rules',
        label: 'Friction Detector/Rewriter Rules',
        value: frictionRules,
        available: Boolean(frictionRules),
        category: 'rules',
      },
      {
        key: 'friction_detector_findings',
        label: 'Friction Detector Findings',
        value: frictionDetectorFindings,
        available: Boolean(frictionDetectorFindings),
        category: 'responses',
      },
      {
        key: 'friction_rewriter_replacements',
        label: 'Friction Rewriter Replacements',
        value: frictionRewriterReplacements,
        available: Boolean(frictionRewriterReplacements),
        category: 'responses',
      },
    ];
  }, [savedProofreadText, sourceChapterText, sourceLang, traceEvents, translatedChapter?.translated_text]);

  const buildAdminTraceCopyPayload = (selectedKeys: string[]) => {
    const sections = adminCopySections.filter(
      (section) => selectedKeys.includes(section.key) && (section.available || section.value),
    );
    return sections
      .map(
        (section) =>
          `${section.label}\n${ADMIN_COPY_SECTION_DIVIDER}\n${section.value}`,
      )
      .join('\n\n');
  };

  const openAdminCopyModal = () => {
    const defaultKeys = adminCopySections
      .filter((section) => section.available)
      .map((section) => section.key);
    if (defaultKeys.length === 0) {
      message.warning('No admin trace sections are available to copy.');
      return;
    }
    setSelectedAdminCopyKeys(defaultKeys);
    setIsAdminCopyModalOpen(true);
  };

  const applyAdminCopyQuickSelection = (
    mode: 'all' | 'none' | 'rules' | 'responses',
  ) => {
    const availableSections = adminCopySections.filter((section) => section.available);
    if (mode === 'none') {
      setSelectedAdminCopyKeys([]);
      return;
    }
    if (mode === 'all') {
      setSelectedAdminCopyKeys(availableSections.map((section) => section.key));
      return;
    }
    setSelectedAdminCopyKeys(
      availableSections
        .filter((section) => section.category === mode)
        .map((section) => section.key),
    );
  };

  const copySelectedAdminSections = async () => {
    const payload = buildAdminTraceCopyPayload(selectedAdminCopyKeys);
    if (!payload.trim()) {
      message.warning('Select at least one section with content to copy.');
      return;
    }
    await copyTraceSectionToClipboard('Admin trace bundle', payload);
    setIsAdminCopyModalOpen(false);
  };

  const totalStageCostSummary = useMemo(
    () => buildTotalStageCostSummary(traceEvents),
    [traceEvents],
  );
  const stageCostCoverageSummary = useMemo(
    () => buildStageCostCoverageSummary(traceEvents),
    [traceEvents],
  );

  const traceCopyButtonStyle: React.CSSProperties = {
    color: 'var(--text-main)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 4,
    width: 20,
    minWidth: 20,
    height: 20,
    padding: 0,
    background: 'var(--surface-base)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  };

  const topTraceCopyButtonStyle: React.CSSProperties = {
    color: 'var(--text-main)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 6,
    minWidth: 24,
    height: 24,
    padding: 0,
    background: 'var(--surface-base)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
  };

  if (!translatedChapter || (!translatedChapter.translated_text && !savedProofreadText)) {
    return (
      <div data-tour-id="chapter-content">
        <span style={{ color: 'var(--text-warning, #d97706)' }}>
          This chapter has not yet been translated.
        </span>
      </div>
    );
  }

  const translationSettingsRows = [
    translationModelValue ? { label: 'Translation Model', value: translationModelValue } : null,
    workflowModeLabel ? { label: 'Setup Mode', value: workflowModeLabel } : null,
    quotationStyleLabel ? { label: 'Quotation Style', value: quotationStyleLabel } : null,
    narrativeStyle ? { label: 'Narrative Style', value: narrativeStyle } : null,
    proofreaderStatusLine ? { label: 'Additional Quality Pass', value: proofreaderStatusLine } : null,
    autoFixSummary ? { label: 'Additional Quality Pass Model(s)', value: autoFixSummary } : null,
    translatedOn ? { label: 'Completed', value: translatedOn } : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;
  const fallbackMessage = 'Translation settings are unavailable for this chapter.';
  const translationSettingsTooltipTitle = (
    <div style={{ maxWidth: 380 }}>
      {translationSettingsRows.length > 0 ? (
        translationSettingsRows.map((entry) => (
          <div key={`${entry.label}-${entry.value}`}>
            <strong>{entry.label}:</strong> {entry.value}
          </div>
        ))
      ) : (
        <div>{fallbackMessage}</div>
      )}
    </div>
  );
  const contentStyle: React.CSSProperties = {
    color: 'var(--text-main)',
    ...(showOrientationToggle && writingDirection === 'vertical'
      ? {
          writingMode: 'vertical-rl',
          textOrientation: 'upright',
          overflowX: 'auto',
          padding: '0 8px',
        }
      : {}),
  };
  const translationHeaderActions = (
    <div className="chapter-content-actions">
      <Tooltip title={translationSettingsTooltipTitle} trigger={['hover', 'click']} placement="left">
        <Button aria-label="Translation settings" icon={<InfoCircleOutlined />} />
      </Tooltip>
      <Tooltip title="Copy Translated Chapter Text to Clipboard">
        <Button onClick={copyToClipboard} type="primary" icon={<CopyOutlined />} />
      </Tooltip>
      <Tooltip title="Delete this Chapter's Translation">
        <Button
          onClick={() => onDelete(sourceLang, targetLang)}
          type="primary"
          icon={<DeleteOutlined />}
          danger
        />
      </Tooltip>
    </div>
  );

  return (
    <div className="chapter-translated-shell">
      {tabActionsContainer && isActive ? createPortal(translationHeaderActions, tabActionsContainer) : null}
      {(showOrientationToggle || !tabActionsContainer) && (
      <div className="chapter-content-toolbar">
        {showOrientationToggle && (
          <Segmented
            size="small"
            value={writingDirection}
            options={[
              { label: 'Horizontal (横書き)', value: 'horizontal' },
              { label: 'Vertical (縦書き)', value: 'vertical' },
            ]}
            aria-label="Japanese text orientation (Horizontal or Vertical)"
            onChange={(value) => setWritingDirection(value as 'horizontal' | 'vertical')}
          />
        )}
        {!tabActionsContainer && translationHeaderActions}
      </div>
      )}
      {translationStaleBadge && (
        <div className="chapter-content-toolbar">
          {translationStaleBadge}
        </div>
      )}
      {canLoadStageTraces && (
        <div
          style={{
            margin: '0 15px 10px',
            padding: '10px',
            border: '1px solid var(--border-muted, #d9d9d9)',
            borderRadius: 8,
            background: 'var(--surface-elevated, #fafafa)',
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span>Admin: Translation Stage Trace</span>
            <Tooltip title="Copy admin trace bundle">
              <Button
                type="default"
                size="small"
                aria-label="Copy admin trace bundle"
                icon={<CopyOutlined />}
                style={topTraceCopyButtonStyle}
                onClick={openAdminCopyModal}
              />
            </Tooltip>
          </div>
          <Modal
            open={isAdminCopyModalOpen}
            title="Copy Admin Trace Sections"
            onCancel={() => setIsAdminCopyModalOpen(false)}
            footer={[
              <Button key="cancel" onClick={() => setIsAdminCopyModalOpen(false)}>
                Cancel
              </Button>,
              <Button
                key="copy"
                type="primary"
                aria-label="Copy selected sections"
                onClick={() => {
                  void copySelectedAdminSections();
                }}
              >
                Copy Selected
              </Button>,
            ]}
          >
            <div style={{ marginBottom: 10, color: 'var(--text-secondary)' }}>
              Choose which sections to include in the copied admin bundle.
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 10,
              }}
            >
              <Button
                size="small"
                aria-label="Select all sections"
                onClick={() => applyAdminCopyQuickSelection('all')}
              >
                Select all
              </Button>
              <Button
                size="small"
                aria-label="Select no sections"
                onClick={() => applyAdminCopyQuickSelection('none')}
              >
                Select none
              </Button>
              <Button
                size="small"
                aria-label="Select rules only"
                onClick={() => applyAdminCopyQuickSelection('rules')}
              >
                Rules only
              </Button>
              <Button
                size="small"
                aria-label="Select responses only"
                onClick={() => applyAdminCopyQuickSelection('responses')}
              >
                Responses only
              </Button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {adminCopySections.map((section) => (
                <Checkbox
                  key={section.key}
                  checked={selectedAdminCopyKeys.includes(section.key)}
                  disabled={!section.available}
                  onChange={(event) => {
                    const checked = Boolean(event?.target?.checked);
                    setSelectedAdminCopyKeys((previous) => {
                      if (checked) {
                        if (previous.includes(section.key)) return previous;
                        return [...previous, section.key];
                      }
                      return previous.filter((key) => key !== section.key);
                    });
                  }}
                >
                  {section.label}
                  {!section.available ? ' (not available)' : ''}
                </Checkbox>
              ))}
            </div>
          </Modal>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
            {traceLoading
              ? 'Loading stage traces...'
              : `Events: ${traceEvents.length}${
                  traceSummary?.runId ? ` | Run ID: ${traceSummary.runId}` : ''
                }${
                  traceSummary?.persistStatus
                    ? ` | Persist: ${String(traceSummary.persistStatus)}`
                    : ''
                }`}
          </div>
          {!traceLoading && totalStageCostSummary && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-main)',
                marginBottom: 8,
                fontWeight: 600,
                background: 'var(--surface-muted, rgba(148, 163, 184, 0.16))',
                border: '1px solid var(--border-subtle, rgba(148, 163, 184, 0.35))',
                borderRadius: 6,
                padding: '6px 8px',
              }}
            >
              All-stage cost summary: {totalStageCostSummary}
            </div>
          )}
          {!traceLoading && stageCostCoverageSummary.unpricedEventCount > 0 && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-warning, #92400e)',
                marginBottom: 8,
                fontWeight: 600,
                background: 'var(--warning-surface, #fff7ed)',
                border: '1px solid var(--warning-border, #fdba74)',
                borderRadius: 6,
                padding: '6px 8px',
              }}
            >
              Cost coverage warning: {stageCostCoverageSummary.unpricedEventCount} stage event
              {stageCostCoverageSummary.unpricedEventCount === 1 ? '' : 's'} had token usage but no
              calculable pricing. Totals above are partial.
              {stageCostCoverageSummary.unpricedStageNames.length > 0
                ? ` Missing pricing stages: ${stageCostCoverageSummary.unpricedStageNames.join(', ')}`
                : ''}
            </div>
          )}
          {traceError && (
            <div style={{ color: 'var(--text-danger, #dc2626)', fontSize: 11, marginBottom: 8 }}>
              {traceError}
            </div>
          )}
          {!traceLoading && traceEvents.length === 0 && !traceError && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              No stage trace events found for this chapter.
            </div>
          )}
          {traceEvents.map((event, index) => {
            const eventId = String(event.id || index);
            const stage = String(event.stage || 'unknown_stage');
            const status = String(event.status || '');
            const stageCostSummary = buildStageCostSummary(event);
            const stageCostUnavailable = hasUnpricedTokenUsage(event);
            const loopCount = buildStageLoopCount(event);
            const systemPrompt = formatAdminTraceValue(event.system_prompt ?? event.systemPrompt);
            const userPrompt = formatAdminTraceValue(event.user_prompt ?? event.userPrompt);
            const rawResponse = formatAdminTraceValue(event.raw_response ?? event.rawResponse);
            const tokenCounts = getEventTokenCounts(event);
            return (
              <details
                key={eventId}
                style={{
                  marginBottom: 8,
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 6,
                  background: 'var(--surface-base)',
                  color: 'var(--text-main)',
                }}
              >
                <summary
                  style={{
                    cursor: 'pointer',
                    padding: '8px 10px',
                    fontSize: 12,
                    fontWeight: 600,
                    userSelect: 'text',
                    background: 'var(--surface-info)',
                    color: 'var(--text-main)',
                  }}
                >
                  {index + 1}. {stage}
                  {status ? ` (${status})` : ''}
                  {` | loops ${loopCount}x`}
                  {event.chunk_id !== undefined ? ` | chunk ${event.chunk_id}` : ''}
                  {event.attempt !== undefined ? ` | attempt ${event.attempt}` : ''}
                  {stageCostSummary ? ` | ${stageCostSummary}` : ''}
                  {!stageCostSummary && stageCostUnavailable ? ' | cost unavailable' : ''}
                </summary>
                <div style={{ padding: '8px 10px', fontSize: 11 }}>
                  <div style={{ marginBottom: 8, color: 'var(--text-secondary)' }}>
                    Model: {String(event.model_id || event.model_id_used || event.configured_model_id || '-')}
                    {' | '}
                    API: {String(event.api_model || '-')}
                    {' | '}
                    Provider: {String(event.provider || '-')}
                  </div>
                  {Object.keys(tokenCounts).length > 0 && (
                    <details style={{ marginBottom: 8 }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: 6 }}>
                        Token Counts
                      </summary>
                      <pre
                        style={{
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          background: 'var(--surface-muted)',
                          color: 'var(--text-main)',
                          padding: 8,
                          borderRadius: 6,
                        }}
                      >
                        {JSON.stringify(tokenCounts, null, 2)}
                      </pre>
                    </details>
                  )}
                  {systemPrompt && (
                    <details style={{ marginBottom: 8 }}>
                      <summary
                        style={{
                          cursor: 'pointer',
                          fontWeight: 600,
                          marginBottom: 6,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span>SYSTEM PROMPT</span>
                        <Tooltip title="Copy system prompt">
                          <Button
                            type="default"
                            size="small"
                            aria-label="Copy system prompt"
                            icon={<CopyOutlined />}
                            style={traceCopyButtonStyle}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              void copyTraceSectionToClipboard('System prompt', systemPrompt);
                            }}
                          />
                        </Tooltip>
                      </summary>
                      <pre
                        style={{
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          background: 'var(--surface-muted)',
                          color: 'var(--text-main)',
                          padding: 8,
                          borderRadius: 6,
                        }}
                      >
                        {systemPrompt}
                      </pre>
                    </details>
                  )}
                  {userPrompt && (
                    <details style={{ marginBottom: 8 }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 600, marginBottom: 6 }}>
                        USER PROMPT
                      </summary>
                      <pre
                        style={{
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          background: 'var(--surface-muted)',
                          color: 'var(--text-main)',
                          padding: 8,
                          borderRadius: 6,
                        }}
                      >
                        {userPrompt}
                      </pre>
                    </details>
                  )}
                  {rawResponse && (
                    <details style={{ marginBottom: 0 }}>
                      <summary
                        style={{
                          cursor: 'pointer',
                          fontWeight: 600,
                          marginBottom: 6,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}
                      >
                        <span>RAW RESPONSE</span>
                        <Tooltip title="Copy raw response">
                          <Button
                            type="default"
                            size="small"
                            aria-label="Copy raw response"
                            icon={<CopyOutlined />}
                            style={traceCopyButtonStyle}
                            onMouseDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                            }}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              void copyTraceSectionToClipboard('Raw response', rawResponse);
                            }}
                          />
                        </Tooltip>
                      </summary>
                      <pre
                        style={{
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          background: 'var(--surface-muted)',
                          color: 'var(--text-main)',
                          padding: 8,
                          borderRadius: 6,
                        }}
                      >
                        {rawResponse}
                      </pre>
                    </details>
                  )}
                </div>
              </details>
            );
          })}
        </div>
      )}
      <div
        style={contentStyle}
        className="chapter-content-card"
        data-tour-id="chapter-content"
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    </div>
  );
};

export default TranslatedTabContent;
